import React, {
  useEffect,
  useState,
} from 'react';

import { dbKidData as db } from '../../services/supabase';
import Card from '../UI/Card';


const PLAN_NAMES = {
  family: 'MyPiggy Family',
  family_plus: 'MyPiggy Family Plus',
};

const formatDate = (value) => {
  if (!value) return '—';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
  }).format(new Date(value));
};

const getPill = (summary) => {
  if (summary.effective_status === 'grace') return 'Grace Period';
  if (summary.effective_status === 'expired') return 'Read Only';
  if (summary.effective_status === 'trial') return 'Free Trial';
  return 'Active';
};

const getMessage = (summary) => {
  const plan = PLAN_NAMES[summary.plan] || 'MyPiggy';
  const organizer = summary.family_organizer_name || 'your Family Organizer';

  switch (summary.status_code) {
    case 'trial_unsubscribed':
      return `Your family's MyPiggy trial ends on ${formatDate(summary.trial_ends_at)}. Ask your Family Organizer, ${organizer}, to choose a plan so everyone can keep using MyPiggy.`;

    case 'trial_subscribed':
      return `Your family has ${plan}! Your free trial lasts until ${formatDate(summary.trial_ends_at)}. Your Family Organizer, ${organizer}, manages your family's MyPiggy plan.`;

    case 'trial_cancellation_scheduled':
    case 'cancellation_scheduled':
      return `Your family's ${plan} plan is available until ${formatDate(summary.cancellation_effective_at)}. Ask your Family Organizer, ${organizer}, if your family would like to keep using MyPiggy.`;

    case 'change_scheduled': {
      const nextPlan = PLAN_NAMES[summary.pending_plan] || plan;
      return `Your family has ${plan} now. ${nextPlan} will begin on ${formatDate(summary.pending_change_effective_at)}. Your Family Organizer, ${organizer}, manages the plan.`;
    }

    case 'payment_grace':
      return `Your family's MyPiggy plan needs attention, but you can keep using MyPiggy until ${formatDate(summary.grace_ends_at)}. Please ask your Family Organizer, ${organizer}, to check the family plan.`;

    case 'expired':
      return `Your family's MyPiggy plan needs to be restarted. You can still view your information, but you cannot make changes. Please ask your Family Organizer, ${organizer}, for help.`;

    case 'active':
      return `Your family has ${plan}! Your Family Organizer, ${organizer}, manages your family's MyPiggy plan.`;

    default:
      return `Your Family Organizer, ${organizer}, manages your family's MyPiggy plan.`;
  }
};

const needsDashboardAttention = (summary) => {
  if ([
    'trial_cancellation_scheduled',
    'cancellation_scheduled',
    'payment_grace',
    'expired',
  ].includes(summary.status_code)) {
    return true;
  }

  if (
    summary.status_code === 'trial_unsubscribed' &&
    summary.trial_ends_at
  ) {
    const remaining =
      new Date(summary.trial_ends_at).getTime() - Date.now();

    return remaining <= 10 * 24 * 60 * 60 * 1000;
  }

  return false;
};

export default function KidSubscriptionCard({
  attentionOnly = false,
}) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const nextSummary = await db.getMyKidBillingSummary();
        if (active) setSummary(nextSummary);
      } catch (loadError) {
        console.error(
          'Unable to load kid subscription status:',
          loadError
        );
        if (active) {
          setError(
            'Your family plan information is unavailable right now.'
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <Card className="p-5">
        <p className="text-sm text-gray-600">
          Loading your family&apos;s MyPiggy plan...
        </p>
      </Card>
    );
  }

  if (!summary) {
    return error ? (
      <Card className="p-5 border-amber-200 bg-amber-50">
        <p className="text-sm text-amber-800">{error}</p>
      </Card>
    ) : null;
  }

  if (attentionOnly && !needsDashboardAttention(summary)) {
    return null;
  }

  const warning =
    summary.effective_status === 'grace' ||
    summary.is_cancellation_scheduled;
  const readOnly = summary.effective_status === 'expired';

  if (attentionOnly) {
    return (
      <Card className="p-4 border-amber-200 bg-amber-50">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-bold text-gray-900">
            Family Plan Update
          </h2>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
            readOnly
              ? 'bg-red-100 text-red-700'
              : 'bg-amber-100 text-amber-800'
          }`}>
            {getPill(summary)}
          </span>
        </div>
        <p className={`mt-2 text-sm font-medium ${
          readOnly ? 'text-red-700' : 'text-amber-800'
        }`}>
          {getMessage(summary)}
        </p>
        <p className="mt-2 text-xs text-gray-600">
          Open Settings to view your family&apos;s plan.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            Your Family&apos;s MyPiggy Plan
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {PLAN_NAMES[summary.plan] || 'MyPiggy'}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
            readOnly
              ? 'bg-red-100 text-red-700'
              : warning
                ? 'bg-amber-100 text-amber-800'
                : 'bg-blue-100 text-blue-700'
          }`}>
            {getPill(summary)}
          </span>

          {summary.status_code === 'change_scheduled' && (
            <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
              Change Scheduled
            </span>
          )}

          {summary.is_cancellation_scheduled && (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
              Ending Soon
            </span>
          )}
        </div>
      </div>

      <div className={`mt-4 rounded-lg border px-4 py-3 text-sm font-medium ${
        readOnly
          ? 'border-red-200 bg-red-50 text-red-700'
          : warning
            ? 'border-amber-200 bg-amber-50 text-amber-800'
            : 'border-blue-200 bg-blue-50 text-blue-800'
      }`}>
        {getMessage(summary)}
      </div>

      {summary.family_organizer_name && (
        <p className="mt-3 text-sm text-gray-700">
          <span className="font-semibold">Family Organizer:</span>{' '}
          {summary.family_organizer_name}
        </p>
      )}
    </Card>
  );
}
